import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

import { toCurrency } from '@espoon-voltti/ts-libs/lib/euro'

import { PublishedApp } from '../../../../domain'
import { t } from '../../../../i18n'

export type StepModel = O.Option<{
  label: string
  status: StepStatus
}>

export type StepStatus =
  | 'NOT_STARTED'
  | 'ACTIVE'
  | 'PENDING'
  | 'COMPLETED'
  | 'DISABLED'

export const appStepModel = (app: PublishedApp): StepModel =>
  O.some({
    label: toCurrency(app.form.appliedSum),
    status: 'COMPLETED'
  })

export const decisionStepModel = (app: PublishedApp): StepModel => {
  switch (app.state) {
    case 'SENT':
      return O.some({ label: '', status: 'NOT_STARTED' })
    case 'REVIEW':
      return O.some({
        label: t(['views', 'preview', 'processSteps', 'DECISION', 'REVIEW']),
        status: 'ACTIVE'
      })
    case 'PRE_REPORT':
    case 'REJECTED':
    case 'PAYMENT_WAIT':
    case 'POST_REPORT':
    case 'PAID':
    case 'RECOVERY':
    case 'RECOVERED':
      return O.some({
        label: t(
          [
            'views',
            'preview',
            'processSteps',
            'DECISION',
            app.decision.decision
          ],
          app.decision.decision === 'REJECTED'
            ? undefined
            : { amount: app.decision.amountInCents.euros() }
        ),
        status: 'COMPLETED'
      })
    default:
      const _: never = app
      return _
  }
}

export const preReportStepModel = (app: PublishedApp): StepModel => {
  switch (app.state) {
    case 'PRE_REPORT':
      return O.some({
        label: t(
          ['views', 'preview', 'processSteps', 'PRE_REPORT', 'PENDING'],
          { dueDate: app.decision.reportDeadlineDate.format('fi') }
        ),
        status: 'PENDING'
      })
    case 'REJECTED':
      return pipe(
        O.fromNullable(app.preReportDecision),
        O.map(() => ({
          label: t([
            'views',
            'preview',
            'processSteps',
            'PRE_REPORT',
            'REJECTED'
          ]),
          status: 'COMPLETED'
        }))
      )
    case 'PAYMENT_WAIT':
    case 'PAID':
      return pipe(
        O.fromNullable(app.preReportDecision),
        O.map((x) => ({
          label: t(
            ['views', 'preview', 'processSteps', 'PRE_REPORT', 'APPROVED'],
            { amount: x.amountInCents.euros() }
          ),
          status: 'COMPLETED'
        }))
      )
    case 'SENT':
    case 'REVIEW':
    case 'POST_REPORT':
    case 'RECOVERY':
    case 'RECOVERED':
      return O.none
    default:
      const _: never = app
      return _
  }
}

export const paymentStepModel = (app: PublishedApp): StepModel => {
  switch (app.state) {
    case 'SENT':
    case 'REVIEW':
      return O.none
    case 'PRE_REPORT':
      return O.some({ label: '', status: 'NOT_STARTED' })
    case 'REJECTED':
      return O.some({
        label: t(['views', 'preview', 'processSteps', 'PAYMENT', 'REJECTED']),
        status: 'DISABLED'
      })
    case 'PAYMENT_WAIT':
      return O.some({
        label: t(['views', 'preview', 'processSteps', 'PAYMENT', 'ACTIVE']),
        status: 'ACTIVE'
      })
    case 'POST_REPORT':
    case 'RECOVERY':
    case 'RECOVERED':
      return O.some({
        label: t(['views', 'preview', 'processSteps', 'PAYMENT', 'PAID'], {
          amount: app.decision.amountInCents.euros()
        }),
        status: 'COMPLETED'
      })
    case 'PAID':
      return O.some({
        label: t(['views', 'preview', 'processSteps', 'PAYMENT', 'PAID'], {
          amount:
            app.decision.decision === 'GRANTED_PAY_AFTER_REPORT'
              ? app.preReportDecision!.amountInCents.euros()
              : app.decision.amountInCents.euros()
        }),
        status: 'COMPLETED'
      })
    default:
      const _: never = app
      return _
  }
}

export const postReportStepModel = (app: PublishedApp): StepModel => {
  switch (app.state) {
    case 'REVIEW':
    case 'SENT':
    case 'PRE_REPORT':
    case 'REJECTED':
      return O.none
    case 'PAYMENT_WAIT':
    case 'POST_REPORT':
      if (app.decision.decision === 'GRANTED_PAY_IMMEDIATELY_WAIT_REPORT') {
        return O.some({
          label: t(
            ['views', 'preview', 'processSteps', 'POST_REPORT', 'ACTIVE'],
            { dueDate: app.decision.reportDeadlineDate.format('fi') }
          ),
          status: app.state === 'POST_REPORT' ? 'PENDING' : 'ACTIVE'
        })
      }
      return O.none
    case 'PAID':
    case 'RECOVERY':
    case 'RECOVERED':
      return pipe(
        O.fromNullable(app.postReportDecision),
        O.map((x) => ({
          label: t([
            'views',
            'preview',
            'processSteps',
            'POST_REPORT',
            x.decision
          ]),
          status: 'COMPLETED'
        }))
      )
    default:
      const _: never = app
      return _
  }
}

export const recoveryStepModel = (app: PublishedApp): StepModel => {
  switch (app.state) {
    case 'SENT':
    case 'REVIEW':
    case 'PRE_REPORT':
    case 'REJECTED':
    case 'PAYMENT_WAIT':
    case 'POST_REPORT':
    case 'PAID':
      return O.none
    case 'RECOVERY':
      return O.some({
        label: t(['views', 'preview', 'processSteps', 'RECOVERY', 'PENDING'], {
          dueDate: app.postReportDecision.dueDate.format('fi'),
          amount: app.postReportDecision.amountInCents.euros()
        }),
        status: 'PENDING'
      })
    case 'RECOVERED':
      return O.some({
        label: t(
          ['views', 'preview', 'processSteps', 'RECOVERY', 'COMPLETED'],
          { amount: app.postReportDecision.amountInCents.euros() }
        ),
        status: 'COMPLETED'
      })
    default:
      const _: never = app
      return _
  }
}
